import { useTranslation } from "react-i18next";
import { WEBHOOK_EVENT_TAXONOMY } from "~/features/webhooks/taxonomy";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** `/^https:\/\/.+/` — the server does not require https; the client does, deliberately: a
 * signing secret delivered over http is a leaked secret. */
const HTTPS_ONLY = /^https:\/\/.+/;

export interface WebhookFormValue {
  url: string;
  events: string[];
}

export function isWebhookFormValid(value: WebhookFormValue): boolean {
  return HTTPS_ONLY.test(value.url.trim()) && value.events.length > 0;
}

function toggleEvent(events: string[], id: string): string[] {
  return events.includes(id) ? events.filter((e) => e !== id) : [...events, id];
}

/**
 * The taxonomy picker, ported exactly (§A.5): ticking `*` replaces the whole selection and
 * disables everything else; ticking a group wildcard disables that group's individual boxes.
 */
export function WebhookForm({
  value,
  onChange,
}: {
  value: WebhookFormValue;
  onChange: (next: WebhookFormValue) => void;
}) {
  const { t } = useTranslation();
  const allSelected = value.events.includes("*");

  return (
    <div className="flex flex-col gap-4">
      <Input
        label={t("Endpoint URL")}
        fullWidth
        value={value.url}
        placeholder="https://"
        onChange={(event) => onChange({ ...value, url: event.target.value })}
        hint={t("Must be https — a signing secret sent over http is a leaked secret.")}
      />

      <div className="flex flex-col gap-3">
        <Text variant="small" className="font-medium text-text-secondary">
          {t("Events")}
        </Text>

        <label className="flex cursor-pointer items-center gap-2 text-body text-text">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => onChange({ ...value, events: allSelected ? [] : ["*"] })}
          />
          {t("All events")}
        </label>

        <div className={cn("flex flex-col gap-3", allSelected && "pointer-events-none opacity-50")}>
          {WEBHOOK_EVENT_TAXONOMY.map((group) => {
            const wildcard = `${group.id}.*`;
            const groupSelected = value.events.includes(wildcard);
            return (
              <div key={group.id} className="flex flex-col gap-1.5 ps-1">
                <label className="flex cursor-pointer items-center gap-2 text-small font-medium text-text-secondary">
                  <input
                    type="checkbox"
                    checked={groupSelected}
                    onChange={() =>
                      onChange({ ...value, events: toggleEvent(value.events, wildcard) })
                    }
                  />
                  {group.id} ({t("all")})
                </label>
                <div
                  className={cn(
                    "flex flex-wrap gap-x-4 gap-y-1 ps-6",
                    groupSelected && "pointer-events-none opacity-50",
                  )}
                >
                  {group.events.map((eventId) => (
                    <label
                      key={eventId}
                      className="flex cursor-pointer items-center gap-1.5 text-small text-text"
                    >
                      <input
                        type="checkbox"
                        checked={groupSelected || value.events.includes(eventId)}
                        onChange={() =>
                          onChange({ ...value, events: toggleEvent(value.events, eventId) })
                        }
                      />
                      <span className="font-mono">{eventId}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
